import * as React from "react";
import {
  Navigate,
  Route,
  Routes,
  useLocation,
  useParams,
} from "react-router-dom";
import { css } from "@emotion/core";
import Authenticate from "./Auth";
import Login from "./Auth/Login";
import ResetPassword from "./Auth/ResetPassword";
import AcceptInvite from "./Auth/AcceptInvite";
import Header from "./Portal/Header";
import KnowledgeBase from "./KnowledgeBase";
import ExportSurvey from "./ExportSurvey";
import { useAuthState } from "context/auth";
import {
  AccessProvider,
  SinkProvider,
  SupportProvider,
  ToastProvider,
  UnitProvider,
} from "context";
import { useSinkState } from "context/sink";
import SurveyOrder from "./SurveyOrder";
import ProtectedRoute from "./common/ProtectedRoute";
import Portal from "./Portal/Portal";
import SurveysNavigator from "./Portal/LeftPane/SurveysNavigator";
import SurveyDetailsNavigator from "./Portal/LeftPane/SurveyDetailsNavigator";
import { FoldersView, SurveysViewWithData } from "./Portal/LeftPane/ListView";
import { OverlaysProvider } from "@blueprintjs/core";
import Support from "./User/Support";
import CreateOrderV2 from "./Orders";
import ChooseOrderType from "./Orders/sections/ChooseOrderType";
import SelectUploads from "./Orders/sections/SelectUploads";
import { OrderMachineContext } from "./Orders/state/orderMachine";
import { AdminLayout } from "./Admin/layout";
import AdminOrganizationUsers from "./Admin/Organization/users";
import AdminOrganizationGroups from "./Admin/Organization/groups";
import AdminOrganizationFolders from "./Admin/Organization/folders";
import AdminOrganizationSettings from "./Admin/Organization/settings";
import AdminInternalOrganizations from "./Admin/Internal/organizations";
import AdminInternalOrganizationEdit from "./Admin/Internal/organizationEdit";
import AdminInternalOrderTypes from "./Admin/Internal/orderTypes";
import AdminInternalChartProperties from "./Admin/Internal/chartProperties";
import { QueryClientProvider, QueryClient } from "@tanstack/react-query";

const queryClient = new QueryClient();

function OrderIndexRedirect() {
  const { id } = useParams();
  const orderId = OrderMachineContext.useSelector(
    (state) => state.context.orderId
  );

  if (orderId) {
    return <Navigate to={`/surveys/${id}/orders/${orderId}`} replace />;
  }

  return <Navigate to={`/surveys/${id}/orders/new`} replace />;
}

function OrderRedirect() {
  const { id, type } = useParams();
  const { sink } = useSinkState();
  const surveyId = parseInt(id || "0");

  const order = sink.orders.find(
    (o) =>
      o.survey_id === surveyId &&
      o.submitted === null &&
      o.completed === null
  );

  // If no in-progress order exists, go to new order flow
  if (!order) {
    return <Navigate to={`/surveys/${id}/orders/new`} replace />;
  }

  // If legacy order (has type, no organization_order_type_id), route to legacy UI
  if (order.type && !order.organization_order_type_id) {
    return <Navigate to={`/surveys/${id}/order/${order.type}`} replace />;
  }

  // If new order, route to new UI
  if (order.organization_order_type_id) {
    return <Navigate to={`/surveys/${id}/orders/${order.id}`} replace />;
  }

  // Fallback to new order flow
  return <Navigate to={`/surveys/${id}/orders/new`} replace />;
}

const App = () => {
  const { currentUser } = useAuthState();
  const { isPendingAuthentication, isAuthenticated } = useAuthState();
  const { pathname } = useLocation();

  const organization_id = currentUser?.organization_id;

  return (
    <main
      css={css`
        display: flex;
        flex-direction: column;
        height: 100%;
        background: var(--background-gradient);
      `}
      className="bp6-dark"
    >
      {isPendingAuthentication == false &&
        (isAuthenticated === false ? (
          <>
            <Header />
            <Routes>
              <Route element={<Authenticate />}>
                <Route path="/login" element={<Login />} />
                <Route path="/resetpassword" element={<ResetPassword />} />
                <Route path="/acceptinvite" element={<AcceptInvite />} />
              </Route>
              <Route
                path="*"
                element={
                  <Navigate to={`/login?redirect=${pathname}`} replace />
                }
              />
            </Routes>
          </>
        ) : (
          <QueryClientProvider client={queryClient}>
            <SinkProvider>
              <AccessProvider>
                <UnitProvider>
                  <OverlaysProvider>
                    <ToastProvider>
                      <SupportProvider>
                        <Header />
                        <Routes>
                          <Route path="/*" element={<ProtectedRoute />}>
                            <Route path="surveys" element={<SurveysNavigator />}>
                              <Route index element={<SurveysViewWithData />} />
                            </Route>
                            <Route path="surveys/:id/*" element={<Portal />}>
                              <Route index element={<SurveyDetailsNavigator />} />
                              <Route path="orders/*" element={<CreateOrderV2 />}>
                                <Route index element={<OrderIndexRedirect />} />
                                <Route path="new" element={<ChooseOrderType />} />
                                <Route path=":order_id" element={<SelectUploads />} />
                              </Route>
                              <Route
                                path="order/:type"
                                element={<SurveyOrder />}
                              />
                              <Route path="order" element={<OrderRedirect />} />
                              <Route path="report" element={<ExportSurvey />} />
                            </Route>
                            <Route
                              path="folders/:id"
                              element={<SurveysNavigator />}
                            >
                              <Route index element={<SurveysViewWithData />} />
                            </Route>
                            <Route path="folders" element={<SurveysNavigator />}>
                              <Route index element={<FoldersView />} />
                            </Route>
                            <Route path="knowledge" element={<KnowledgeBase />} />
                            <Route path="admin" element={<AdminLayout />}>
                              <Route
                                path="organizations/:organization_id/settings"
                                element={<AdminOrganizationSettings />}
                              />
                              <Route
                                path="organizations/:organization_id/users"
                                element={<AdminOrganizationUsers />}
                              />
                              <Route
                                path="organizations/:organization_id/groups"
                                element={<AdminOrganizationGroups />}
                              />
                              <Route
                                path="organizations/:organization_id/folders"
                                element={<AdminOrganizationFolders />}
                              />
                              <Route
                                index
                                element={
                                  <Navigate
                                    to={`/admin/organizations/${organization_id}/settings`}
                                    replace
                                  />
                                }
                              />
                            </Route>
                            <Route path="internal" element={<AdminLayout />}>
                              <Route
                                path="organizations"
                                element={<AdminInternalOrganizations />}
                              />
                              <Route
                                path="organizations/:id/edit"
                                element={<AdminInternalOrganizationEdit />}
                              />
                              <Route
                                path="order-templates"
                                element={<AdminInternalOrderTypes />}
                              />
                              <Route
                                path="order-types"
                                element={
                                  <Navigate to="/internal/order-templates" replace />
                                }
                              />
                              <Route
                                path="chart-properties"
                                element={<AdminInternalChartProperties />}
                              />
                              <Route
                                index
                                element={
                                  <Navigate
                                    to="/internal/organizations"
                                    replace
                                  />
                                }
                              />
                            </Route>
                            <Route
                              path="*"
                              element={<Navigate to="/folders" replace />}
                            />
                          </Route>
                        </Routes>
                        <Support />
                      </SupportProvider>
                    </ToastProvider>
                  </OverlaysProvider>
                </UnitProvider>
              </AccessProvider>
            </SinkProvider>
          </QueryClientProvider>
        ))}
    </main>
  );
};

export default App;
